import flatpickr from "flatpickr";
import { showAge, validDate } from "./date_of_birth";
import { FLATPICKR_LOCALES } from "./constants";
import { FormValidator } from "./form_validator/form_validator";
import { formatDate } from "./utils";
import { updateSelectStartDateOptions } from "./validation/score/score_utils";

export function detectDateFormat(fullBrowserLocale) {
    try {
        const date = new Date(2000, 0, 2);

        const supportedLocales = Intl.DateTimeFormat.supportedLocalesOf([
            fullBrowserLocale,
        ]);

        if (supportedLocales.length === 0) {
            throw new Error("Invalid locale");
        }

        const formatter = new Intl.DateTimeFormat(fullBrowserLocale, {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        });
        const parts = formatter.formatToParts(date);

        const flatpickrFormatParts = [];
        const placeholderFormatParts = [];
        let separator = "/";

        parts.forEach((part) => {
            if (part.type === "day") {
                flatpickrFormatParts.push("d");
                placeholderFormatParts.push("dd");
            } else if (part.type === "month") {
                flatpickrFormatParts.push("m");
                placeholderFormatParts.push("mm");
            } else if (part.type === "year") {
                flatpickrFormatParts.push("Y");
                placeholderFormatParts.push("yyyy");
            } else if (
                part.type === "literal" &&
                [".", "/", "-"].includes(part.value)
            ) {
                separator = part.value;
            }
        });

        return {
            flatpickrFormat: flatpickrFormatParts.join(separator),
            placeholderFormat: placeholderFormatParts.join(separator),
            separator: separator,
        };
    } catch (e) {
        console.warn("Error detecting date format:", e);
        return {
            flatpickrFormat: "d/m/Y",
            placeholderFormat: "dd/mm/yyyy",
            separator: "/",
        };
    }
}

export function getFlatpickrLocale(fullBrowserLocale) {
    const [browserLocale] = fullBrowserLocale.split("-");
    const flatpickrLocale = FLATPICKR_LOCALES[browserLocale] || "default";

    return flatpickrLocale;
}

function validateField(element) {
    const form = FormValidator.getInstance("submit-guide-form");
    const field = form.getField(element.dataset.validator);
    form.validateField(field);
}

function isMeyeGuide() {
    return document.getElementById("meye-guide-page") !== null;
}

function isMeyeGauge() {
    return document.getElementById("meye-gauge-page") !== null;
}

export function dateParser(datestr, format) {
    if (!datestr) return null;

    datestr = datestr.trim();
    const parts = datestr.split(/[/.-]/);
    const formatParts = format.split(/[/.-]/);

    if (parts.length !== 3 || formatParts.length !== 3) return null;

    let day, month, year;

    for (let i = 0; i < 3; i++) {
        const token = formatParts[i];
        const part = parts[i];

        if (token.includes("d")) {
            day = parseInt(part, 10);
        } else if (token.includes("m")) {
            month = parseInt(part, 10);
        } else if (token.includes("Y") || token.includes("y")) {
            year = part;
        }
    }

    // Reject if any part is missing or invalid
    if (!day || !month || !year || !/^\d{2}$|^\d{4}$/.test(year)) return null;

    // Handle 2-digit years
    if (year.length === 2) {
        const yearNum = parseInt(year, 10);
        year = yearNum < 50 ? "20" + year : "19" + year;
    }

    const yearNum = parseInt(year, 10);

    // Reject out-of-range components early
    if (
        isNaN(day) ||
        isNaN(month) ||
        isNaN(yearNum) ||
        month < 1 ||
        month > 12 ||
        day < 1 ||
        day > 31 ||
        yearNum < 1000 ||
        yearNum > 9999
    ) {
        return null;
    }

    // Use ISO string to avoid timezone quirks and validate real calendar date
    const isoDateString = `${yearNum.toString().padStart(4, "0")}-${String(
        month
    ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const parsedDate = new Date(isoDateString);

    // Validation: check if resulting date matches original components
    if (
        parsedDate.getFullYear() !== yearNum ||
        parsedDate.getMonth() + 1 !== month ||
        parsedDate.getDate() !== day
    ) {
        return null;
    }

    return parsedDate;
}

export function createDateChangeHandler(hiddenInput, showAgeFn) {
    return (selectedDates, _dateStr, instance) => {
        if (selectedDates?.[0]) {
            const isoDate = selectedDates[0].toISOString().split("T")[0];
            hiddenInput.value = isoDate;
            if (!isMeyeGauge()) {
                showAgeFn();
            }
        } else {
            hiddenInput.value = "";
        }

        updateYearSelectSelection(instance);
    };
}

export function createDateCloseHandler(
    hiddenInput,
    visibleInput,
    showAgeFn,
    validateFn
) {
    return (selectedDates, _dateStr, instance) => {
        if (selectedDates?.[0]) {
            const isoDate = selectedDates[0].toISOString().split("T")[0];
            hiddenInput.value = isoDate;
        } else {
            hiddenInput.value = "";
        }

        if (isMeyeGuide()) {
            validateFn(visibleInput);
        }
        if (!isMeyeGauge()) {
            showAgeFn();
        }

        updateYearSelectSelection(instance);
    };
}

export function createYearSelect(instance) {
    const currentYear = new Date().getFullYear();
    const minYear = 2000;
    const maxYear = currentYear;
    const yearInput = instance.calendarContainer.querySelector(
        ".numInputWrapper > input"
    );
    const yearSelect = document.createElement("select");

    yearSelect.classList.add("flatpickr-year-select");

    for (let y = maxYear; y >= minYear; y--) {
        const option = document.createElement("option");
        option.value = y;
        option.textContent = y;
        if (y === instance.currentYear) option.selected = true;
        yearSelect.appendChild(option);
    }

    const wrapper = yearInput.parentElement;
    wrapper.innerHTML = "";
    wrapper.appendChild(yearSelect);

    yearSelect.addEventListener("change", function () {
        const selectedYear = parseInt(this.value);
        instance.changeYear(selectedYear);
    });
}

export function updateYearSelectSelection(instance) {
    const yearSelect = instance?.calendarContainer?.querySelector(
        ".flatpickr-year-select"
    );
    if (yearSelect) {
        const newYear = instance.currentYear;
        if (parseInt(yearSelect.value) !== newYear) {
            yearSelect.value = newYear;
        }
    }
}

export function attachCalendarIconListener(instance) {
    const inputId = instance.input.id;
    const label = document.querySelector(`label[for="${inputId}"]`);

    if (label) {
        label.addEventListener("click", () => {
            // open flatpickr calendar
            instance.open();
        });
    }
}

function setAltInputId(instance, suffix = "alt") {
    if (instance.input.id) {
        instance.altInput.id = `${instance.input.id}-${suffix}`;
    }
}

function setInternalInputsName(instance, prefix = "flatpickr") {
    const inputs = instance.calendarContainer.querySelectorAll("input,select");

    inputs.forEach((input) => {
        if (!input.id && !input.name) {
            const namePrefix =
                instance.input?.id ?? instance.input?.name ?? prefix;
            input.name = `${namePrefix}-input`;
        }
    });
}

function initFlatPickrInput(selectedDates, dateStr, instance, handlers) {
    createYearSelect(instance);
    attachCalendarIconListener(instance);
    setAltInputId(instance);
    setInternalInputsName(instance);

    if (handlers && instance.altInput) {
        instance.altInput.addEventListener("input", handlers.input);
        instance.altInput.addEventListener("keydown", handlers.keydown);
    }
}

export function createAutoSeparatorHandler(format, separator) {
    const formatParts = format.replace(/[/.-]/g, "").split("");
    const yearIndex = formatParts.indexOf("Y");
    const isYearFirst = yearIndex === 0;

    if (isYearFirst) {
        return null;
    }

    // Track if user is in manual mode (typing single digits with separators)
    let manualMode = false;

    // Helper function to check if user typed separator after single digit
    const detectManualMode = (value, cursorPos) => {
        const beforeSeparator = value.slice(0, cursorPos - 1);
        const lastPart = beforeSeparator.split(separator).pop() || "";
        const digitsInLastPart = (lastPart.match(/\d/g) || []).length;
        return digitsInLastPart === 1;
    };

    // Helper function to handle duplicate separator removal
    const removeDuplicateSeparator = (input, value, cursorPos) => {
        if (value[cursorPos - 2] === separator) {
            input.value =
                value.slice(0, cursorPos - 1) + value.slice(cursorPos);
            input.setSelectionRange(cursorPos - 1, cursorPos - 1);
        }
    };

    // Helper function to check if auto-formatting should occur in manual mode
    const shouldAutoFormatInManualMode = (value) => {
        const valueParts = value.split(separator);
        const lastValuePart = valueParts[valueParts.length - 1] || "";
        const digitsInLastPart = (lastValuePart.match(/\d/g) || []).length;
        return digitsInLastPart === 2;
    };

    // Helper function to handle manual mode formatting
    const handleManualModeFormatting = (input, value, cursorPos) => {
        const valueParts = value.split(separator);
        const lastPart = valueParts[valueParts.length - 1] || "";
        const digitsInLastPart = (lastPart.match(/\d/g) || []).length;

        const shouldAddSeparator =
            digitsInLastPart === 2 &&
            !value.endsWith(separator) &&
            valueParts.length < 3;

        if (shouldAddSeparator) {
            input.value = value + separator;
            input.setSelectionRange(cursorPos + 1, cursorPos + 1);
        }
    };

    // Helper function to handle auto mode formatting
    const handleAutoModeFormatting = (input, value, cursorPos) => {
        const digitOnly = value.replace(/\D/g, "");
        const parts = digitOnly.match(/.{1,2}/g) || [];
        let newValue = "";
        let constructedCursorPos = cursorPos;

        for (let i = 0; i < parts.length; i++) {
            newValue += parts[i];
            if (i < 2 && parts[i].length === 2) {
                newValue += separator;
                if (cursorPos >= newValue.length - 1) {
                    constructedCursorPos++;
                }
            }
        }

        if (value !== newValue) {
            input.value = newValue;
            input.setSelectionRange(constructedCursorPos, constructedCursorPos);
        }
    };

    // Helper function to handle manually typed separator
    const handleManualSeparator = (input, value, cursorPos) => {
        if (detectManualMode(value, cursorPos)) {
            manualMode = true;
        }
        removeDuplicateSeparator(input, value, cursorPos);
    };

    const inputHandler = function (event) {
        const input = event.target;
        const { inputType, data } = event;

        if (!inputType?.includes("insert")) return;

        const { value } = input;
        const cursorPos = input.selectionStart;

        // Handle manually typed separator
        if (data === separator) {
            handleManualSeparator(input, value, cursorPos);
            return;
        }

        // Auto-format only when digits are typed
        if (data && !/\d/.test(data)) return;

        // Determine if we should auto-format
        const shouldAutoFormat =
            !manualMode || shouldAutoFormatInManualMode(value);
        if (!shouldAutoFormat) return;

        // Apply appropriate formatting
        if (manualMode) {
            handleManualModeFormatting(input, value, cursorPos);
        } else {
            handleAutoModeFormatting(input, value, cursorPos);
        }
    };

    const keydownHandler = function (event) {
        if (event.key !== "Backspace") return;

        const input = event.target;
        const cursorPos = input.selectionStart;
        const { value } = input;

        // Reset manual mode if user clears the field
        if (value.length <= 1) {
            manualMode = false;
        }

        if (cursorPos < 2) return;

        const charBefore = value[cursorPos - 1];
        const charTwoBefore = value[cursorPos - 2];

        const shouldRemoveDigitWithSeparator =
            charBefore === separator && /\d/.test(charTwoBefore);

        if (shouldRemoveDigitWithSeparator) {
            event.preventDefault();
            const newValue =
                value.slice(0, cursorPos - 2) + value.slice(cursorPos);
            input.value = newValue;
            input.setSelectionRange(cursorPos - 2, cursorPos - 2);
        }
    };

    return {
        input: inputHandler,
        keydown: keydownHandler,
    };
}

export function initFlatPickrDOB() {
    const hiddenInput = document.getElementById("dob-date");

    if (!hiddenInput) {
        return;
    }

    const visibleInput = document.getElementById("dob-date-visible");
    const fullBrowserLocale = navigator.language;
    const { flatpickrFormat, placeholderFormat, separator } =
        detectDateFormat(fullBrowserLocale);

    visibleInput.placeholder = placeholderFormat;

    const locale = getFlatpickrLocale(fullBrowserLocale);

    const handlers = createAutoSeparatorHandler(flatpickrFormat, separator);

    flatpickr(visibleInput, {
        allowInput: true,
        dateFormat: "Y-m-d",
        locale: locale,
        altInput: true,
        altFormat: flatpickrFormat,
        parseDate: dateParser,
        onReady: function (selectedDates, dateStr, instance) {
            initFlatPickrInput(selectedDates, dateStr, instance, handlers);
        },
        onChange: createDateChangeHandler(hiddenInput, showAge),
        onClose: createDateCloseHandler(
            hiddenInput,
            visibleInput,
            showAge,
            validateField
        ),
    });
}

export function initFlatPickrVisitDate() {
    const hiddenInput = document.getElementById("visit_date");

    if (!hiddenInput) {
        return;
    }

    const visibleInput = document.getElementById("visit-date-visible");
    const fullBrowserLocale = navigator.language;
    const { flatpickrFormat, placeholderFormat, separator } =
        detectDateFormat(fullBrowserLocale);
    const locale = getFlatpickrLocale(fullBrowserLocale);

    visibleInput.placeholder = placeholderFormat;

    const handlers = createAutoSeparatorHandler(flatpickrFormat, separator);

    flatpickr(visibleInput, {
        allowInput: true,
        dateFormat: "Y-m-d",
        defaultDate: "today",
        locale: locale,
        altInput: true,
        altFormat: flatpickrFormat,
        parseDate: dateParser,
        onReady: function (selectedDates, dateStr, instance) {
            initFlatPickrInput(selectedDates, dateStr, instance, handlers);
        },
        onChange: createDateChangeHandler(hiddenInput, showAge),
        onClose: createDateCloseHandler(
            hiddenInput,
            visibleInput,
            showAge,
            validateField
        ),
    });
}

export function initTreatmentStopDate() {
    const stopDateInput = document.getElementById("stop-date");

    if (!stopDateInput) {
        return;
    }

    const fullBrowserLocale = navigator.language;
    const { flatpickrFormat, placeholderFormat, separator } =
        detectDateFormat(fullBrowserLocale);
    const locale = getFlatpickrLocale(fullBrowserLocale);

    stopDateInput.value = "";
    stopDateInput.placeholder = placeholderFormat;

    const handlers = createAutoSeparatorHandler(flatpickrFormat, separator);

    flatpickr(stopDateInput, {
        allowInput: true,
        dateFormat: "Y-m-d",
        locale: locale,
        altInput: true,
        altFormat: flatpickrFormat,
        parseDate: dateParser,
        onReady: function (selectedDates, dateStr, instance) {
            initFlatPickrInput(selectedDates, dateStr, instance, handlers);
        },
    });
}

export function initTreatmentStartDate() {
    const startDateInput = document.getElementById("start-date");

    if (!startDateInput) {
        return;
    }

    const fullBrowserLocale = navigator.language;
    const { flatpickrFormat, placeholderFormat, separator } =
        detectDateFormat(fullBrowserLocale);
    const locale = getFlatpickrLocale(fullBrowserLocale);

    startDateInput.placeholder = placeholderFormat;

    const handlers = createAutoSeparatorHandler(flatpickrFormat, separator);

    flatpickr(startDateInput, {
        allowInput: true,
        dateFormat: "Y-m-d",
        locale: locale,
        altInput: true,
        altFormat: flatpickrFormat,
        parseDate: dateParser,
        onReady: function (selectedDates, dateStr, instance) {
            initFlatPickrInput(selectedDates, dateStr, instance, handlers);
        },
        onChange: (selectedDates) => {
            if (selectedDates?.[0]) {
                const container = startDateInput.closest(".select-start-date");

                if (container) {
                    const select = container.querySelector("select");
                    const dateContainer =
                        container.querySelector(".date-container");
                    const dateInput = dateContainer.querySelector(
                        "input[name=start-date]"
                    );
                    if (!validDate(dateInput.value)) {
                        return;
                    }

                    // Remove any existing hidden custom option before adding the new one
                    const existingCustomOption = select.querySelector(
                        "#option-custom-date"
                    );
                    if (existingCustomOption) {
                        select.removeChild(existingCustomOption);
                    }

                    const formattedDate = formatDate(dateInput.value);
                    // Create or update the custom date option
                    const customOption = new Option(
                        formattedDate,
                        dateInput.value,
                        true,
                        true
                    );
                    customOption.id = "option-custom-date";
                    customOption.hidden = true;

                    select.appendChild(customOption);
                    select.value = dateInput.value;
                    dateContainer.classList.add("d-none");

                    select.classList.remove("custom-date-visible");
                    dateContainer.classList.add("d-none");
                }
            } else {
                startDateInput.value = "";
            }
        },
    });
}

function onUpdateScoreVisitDate(instance) {
    updateSelectStartDateOptions();
    updateYearSelectSelection(instance);
}

export function initScoreVisitDates() {
    if (isMeyeGauge() || isMeyeGuide()) {
        // Skip initialization if on MeyeGauge or MeyeGuide pages
        return;
    }

    const inputs = document.querySelectorAll("input[id^='visit-date']");
    if (inputs.length === 0) return;

    const fullBrowserLocale = navigator.language;
    const { flatpickrFormat, placeholderFormat, separator } =
        detectDateFormat(fullBrowserLocale);
    const locale = getFlatpickrLocale(fullBrowserLocale);
    const handlers = createAutoSeparatorHandler(flatpickrFormat, separator);

    inputs.forEach((input) => {
        input.placeholder = placeholderFormat;

        flatpickr(input, {
            allowInput: true,
            dateFormat: "Y-m-d",
            locale: locale,
            altInput: true,
            altFormat: flatpickrFormat,
            parseDate: dateParser,
            onReady: function (selectedDates, dateStr, instance) {
                initFlatPickrInput(selectedDates, dateStr, instance, handlers);
            },
            onChange: (_selectedDates, _dateStr, instance) =>
                onUpdateScoreVisitDate(instance),
            onClose: (_selectedDates, _dateStr, instance) =>
                onUpdateScoreVisitDate(instance),
        });
    });
}
